import AssignCasesAutocomplete from "@/components/Forms/AssignCasesAutocomplete.tsx";
import AssignCasesDisplay from "@/components/Forms/AssignCasesDisplay.tsx";
import TaskTemplateAutocomplete from "@/components/Forms/TaskTemplateAutocomplete.tsx";
import { addTaskToSelfByTaskTemplateSchema } from "@/components/Forms/schema/addCaseTasksSchema.ts";
import { useAddCaseTaskToSelfByTaskTemplate } from "@/mutations/useAddCaseTaskToSelfByTaskTemplate.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 as Grid } from "@mui/material";
import dayjs from "dayjs";
import { RhfDatePicker } from "mui-rhf-integration/date-picker";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useMemo } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export type AddTaskToSelfByTaskTemplateFormValues = z.infer<
    typeof addTaskToSelfByTaskTemplateSchema
>;

type Props = {
    caseIdUUID?: string;
    foundCaseId?: string;
};
const AssignTasksToSelfForm = ({ caseIdUUID, foundCaseId }: Props): ReactNode => {
    const addTask = useAddCaseTaskToSelfByTaskTemplate(caseIdUUID);

    const form = useForm<AddTaskToSelfByTaskTemplateFormValues>({
        resolver: zodResolver(addTaskToSelfByTaskTemplateSchema),
        defaultValues: {
            cases: [],
            duration: 0, // Will be calculated from due date if provided
        },
    });

    const due = form.watch("due");

    const differenceInDays = useMemo(() => {
        const now = dayjs();

        if (dayjs.isDayjs(due) && dayjs.isDayjs(now)) {
            return due.diff(now, "day") + 1;
        }

        // Return 0 if no due date is set (for tasks without due dates)
        return due ? 1 : 0;
    }, [due]);

    const handleSubmit = (values: AddTaskToSelfByTaskTemplateFormValues) => {
        addTask
            .mutateAsync({
                ...values,
                duration: differenceInDays,
            })
            .then(() => {
                enqueueSnackbar("Task added to case(s).", { variant: "success" });

                handleClose();
            })
            .catch(() => {
                enqueueSnackbar("Error: Add Task failed.", { variant: "error" });
            });
    };
    const handleClose = () => {
        form.reset({
            cases: [],
            duration: 0,
        });
    };

    const onSubmit: SubmitHandler<AddTaskToSelfByTaskTemplateFormValues> = async (
        data: AddTaskToSelfByTaskTemplateFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    return (
        <FormProvider {...form}>
            <form>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <AssignCasesAutocomplete
                            limitToCurrentEmployee={true}
                            foundCaseId={foundCaseId}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <AssignCasesDisplay />
                    </Grid>
                    <Grid size={{ xs: 6 }} sx={{ pt: 1, py: 2 }}>
                        <TaskTemplateAutocomplete />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <RhfDatePicker
                            control={form.control}
                            name={"due"}
                            label={"Due Date"}
                            views={["year", "month", "day"]}
                            sx={{
                                width: "100%",
                                backgroundColor: "#e9ecef",
                                maxWidth: "300px",
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ pt: 1, textAlign: "center" }}>
                        <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                            Assign Tasks
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </FormProvider>
    );
};

export default AssignTasksToSelfForm;
